import { useEffect, useState } from 'react';
import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { locationTypes } from '../../constants/statusOptions';
import { getSites } from '../../services/siteService';
import { updateLocation } from '../../services/locationService';
import type { Site, SiteLocationRow } from '../../types/project';

interface EditLocationDialogProps {
  open: boolean;
  location: SiteLocationRow | null;
  siteKey: string;
  onClose: () => void;
  onUpdated: () => void;
}

export const EditLocationDialog = ({
  open, location, siteKey, onClose, onUpdated,
}: EditLocationDialogProps) => {
  const [locationId, setLocationId] = useState('');
  const [locationName, setLocationName] = useState('');
  const [locationType, setLocationType] = useState('Borehole');
  const [selectedSite, setSelectedSite] = useState<Site | null>(null);
  const [allSites, setAllSites] = useState<Site[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (location && open) {
      setLocationId(location.LocationId);
      setLocationName(location.LocationName);
      setLocationType(location.LocationType);
      setError('');
      // Load all sites so user can move location to a different site
      getSites().then((sites) => {
        setAllSites(sites);
        const current = sites.find((s) => s.SiteKey === siteKey);
        setSelectedSite(current ?? null);
      }).catch(() => {});
    }
  }, [location, open, siteKey]);

  const handleSubmit = async () => {
    if (!location) return;
    if (!locationId.trim()) { setError('Location ID is required'); return; }
    if (!locationName.trim()) { setError('Location Name is required'); return; }
    if (!selectedSite) { setError('Site is required'); return; }
    setSubmitting(true);
    setError('');
    try {
      await updateLocation(location.LocationKey, {
        LocationId: locationId.trim(),
        LocationName: locationName.trim(),
        LocationType: locationType,
        SiteKey: selectedSite.SiteKey,
      });
      onUpdated();
    } catch {
      setError('Failed to update location.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!location) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>Edit Location</DialogTitle>
      <Divider />
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2.5 }}>
        <TextField size="small" label="Location ID" required value={locationId}
          onChange={(e) => setLocationId(e.target.value)} />
        <TextField size="small" label="Location Name" required value={locationName}
          onChange={(e) => setLocationName(e.target.value)} />
        <TextField size="small" select label="Location Type" required value={locationType}
          onChange={(e) => setLocationType(e.target.value)}>
          {locationTypes.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
        </TextField>
        <Autocomplete
          size="small"
          options={allSites}
          getOptionLabel={(opt) => `${opt.SiteName} (${opt.SiteCity})`}
          value={selectedSite}
          onChange={(_e, val) => setSelectedSite(val)}
          isOptionEqualToValue={(opt, val) => opt.SiteKey === val.SiteKey}
          renderInput={(params) => (
            <TextField {...params} label="Site" required
              helperText="Change the site to move this location" />
          )}
        />
        {error && <Typography variant="body2" color="error">{error}</Typography>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}
          sx={{ textTransform: 'none', fontWeight: 600, px: 3, borderRadius: 1.5 }}
          startIcon={submitting ? <CircularProgress size={16} /> : undefined}>
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};
